import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../layout/Layout";
import { apiV2 } from "../../../api/axiosConfig";
import BasicText from "../../../components/form-v2/basic-text";
import BasicSelect from "../../../components/form-v2/basic-select";
import { BlueButton } from "../../../components/ui/Buttons";
import { DepartmentSelectSingle } from "../../../components/form-v2/searchable-select/DepartmentSelect";
import { RoleSelect } from "../../../components/form-v2/searchable-select/RoleSelect";
import { ClientSelectSingle } from "../../../components/form-v2/searchable-select/ClientSelect";
import { useAuth } from "../../../context/user";
import { useUserManagementBrowsing } from "../../../lib/user-management/UserManagementBrowsingContext";
import { InfoOutlined } from "@mui/icons-material";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { isAxiosError } from "axios";
import UserSelect from "../../../components/form-v2/searchable-select/UserSelect";
import UserSelectSingleV2 from "../../../components/form-v2/searchable-select/UserSelectSingleV2";
import { UserDataTypes } from "../../../types/user-data-types";
import { Department } from "../../../types/userManagement";
import { useTimeZoneSelectContext } from "@/providers/TimeZoneSelectProvider";
import moment from "moment";


const DeviceEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const browsing = useUserManagementBrowsing();

  const [name, setName] = useState("");
  const [deviceSource, setDeviceSource] = useState<string|undefined>("null")
  const [deviceSourceAPI, setDeviceSourceAPI] = useState<string | undefined>("null");
  const [deviceType, setDeviceType] = useState<string | undefined>("null");
  const [imei, setImei] = useState("");
  const [simCardPhoneNum, setSimCardPhoneNum] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [sourceID, setSourceID] = useState("");
  const [secondary, setSecondary] = useState(false);
  const [oemSerial, setOemSerial] = useState("");
  const [apiHubSerial, setApiHubSerial] = useState("");
  const [apiSource, setApiSource] = useState("");
  const [department, setDepartment] = useState<Department | undefined>();
  const [departmentID, setDepartmentID] = useState("");
  const [deviceID, setDeviceID] = useState("");
  const [useNameAsEverywhereHub, setUseNameAsEverywhereHub] = useState(false);
  const [clientID, setClientID] = useState("");
  const [assignedUserID, setAssignedUserID] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserDataTypes | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [endDate, setEndDate] = useState<string>("")
  const [assignmentEndDateTouched, setAssignmentEndDateTouched] = useState(false);

  const initialClientID = useMemo(() => browsing.clientID, [browsing.clientID]);
  const tzCtx = useTimeZoneSelectContext()
  const timezone = tzCtx.timeZone

  
  const handleUserChange = (user: UserDataTypes | null) => {
    if (user?.id !== selectedUser?.id && selectedUser) {
      setEndDate("")
      setAssignmentEndDateTouched(true)
    }
    setSelectedUser(user)
  }
  



  const infoArrayToObject = (infoArray: { key: string; value: string }[] = []) => {
    return infoArray.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as Record<string, string>);
  };

  useEffect(() => {
    if (!id) return;
    apiV2
      .get(`device/${id}`)
      .then((response) => {
        const device = response.data;
        const info = infoArrayToObject(device.info); 
        console.log(info)
        setName(device.name);
        setDeviceSource(device.type)
        setDeviceType(device.type);
        setImei(info.imei || "");
        setSimCardPhoneNum(info.simCardPhoneNum || "");
        setMake(info.make || "");
        setModel(info.model || "");
        setSourceID(info.sourceID || "");
        setSecondary(info.secondary === "yes");
        setOemSerial(info.oemSerial || "");
        setApiHubSerial(info.apiHubSerial || "");
        setApiSource(info.apiSource || "");
        setAssignedUserID(device.userID);
        setEndDate(device.assignmentEndDate && device.assignmentEndDate !== "0001-01-01T00:00:00Z"
        ? device.assignmentEndDate.slice(0, 10)
        : ""
        );
        setDeviceSourceAPI(device.deviceSourceAPI)
        setAssignmentEndDateTouched(false);
        setDepartmentID(device.departmentID);
        setDeviceID(device.deviceID);
        setUseNameAsEverywhereHub(device.deviceID === device.name);
        setClientID(device.clientID);
        setLoading(false);
      })
      .catch(() => {
        setErrors(["Error loading device"]);
        setLoading(false);
      });
  }, [id]);

  const info = useMemo(() => {
    if (deviceType !== "satellite-everywhere") return [];
    return [
      { key: "imei", value: imei },
      { key: "simCardPhoneNum", value: simCardPhoneNum },
      { key: "make", value: make },
      { key: "model", value: model },
      { key: "sourceID", value: sourceID },
      { key: "secondary", value: secondary ? "yes" : "no" },
      { key: "oemSerial", value: oemSerial },
      { key: "apiHubSerial", value: apiHubSerial },
      { key: "apiSource", value: apiSource },
    ];
  }, [
    deviceType,
    deviceSourceAPI,
    imei,
    simCardPhoneNum,
    make,
    model,
    sourceID,
    secondary,
    oemSerial,
    apiHubSerial,
    apiSource,
  ]);


  const save = useCallback(async () => {
    setSaving(true);
    setErrors([]);
  
    const finalDeviceID = useNameAsEverywhereHub ? name : deviceID;
  
    if (!finalDeviceID || finalDeviceID.trim() === "") {
      setErrors(["Device ID is required"]);
      setSaving(false);
      return;
    }
  
    if (
      deviceSource === "satellite-everywhere" &&
      deviceType === "satellite-everywhere" &&
      deviceSourceAPI === "everywhere-mission-connect"
    ) {
      const imeiRegex = /^[0-9]{15}$/;
      if (!imeiRegex.test(finalDeviceID)) {
        setErrors(["Invalid IMEI Device ID"]);
        setSaving(false);
        return;
      }
    }
  
    try {
      const patchPayload: any = {
        name,
        type: deviceType,
        deviceSourceAPI,
        departmentID: department?.id,
        clientID: clientID,
        deviceID: finalDeviceID,
        userID: selectedUser?.id,
        info,
      };
      if (assignmentEndDateTouched) {
        patchPayload.assignmentEndDate = moment.tz(endDate, timezone).toISOString();
      }
      await apiV2.patch(`device/${id}`, patchPayload);
  
      setSaving(false);
      const params = new URLSearchParams();
      params.set("message", "device has been updated");
      navigate(`/management/devices?${params.toString()}`);
    } catch (e) {
      if (isAxiosError(e) && e?.response?.data?.error) {
        setErrors([e.response.data.error]);
      } else if (isAxiosError(e) && e?.response?.data?.errors) {
        setErrors(e.response.data.errors);
      } else {
        setErrors(["An error has occurred"]);
      }
      setSaving(false);
    }
  }, [
    name,
    deviceType,
    deviceSourceAPI,
    department,
    clientID,
    deviceID,
    useNameAsEverywhereHub,
    selectedUser,
    info,
    id,
    navigate,
    endDate,
    assignmentEndDateTouched,
    timezone,
    deviceSource,
  ]);
  
  if (loading) {
    return (
      <Layout active="devices">
        <div className="flex flex-row gap-8">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }
  console.log("assigneduserid", assignedUserID)
  return (
    <Layout active="devices">
      <div className="flex flex-row gap-8 mb-8">
        <div className="flex flex-col flex-1 gap-8 mb-8">
          <div className="bg-white shadow-md p-4 flex flex-col gap-3">
            <p className="text-md text-left pb-2 font-semibold">Edit Device</p>
            <BasicText value={name} onChange={setName} label="Name" required />
            <BasicSelect
              label="Source"
              value={deviceSource}
              onChange={setDeviceSource}
              required
              options={[
                { value: "null", label: "Please Select a Source" },
                { value: "satellite-everywhere", label: "Everywhere" },
              ]}
            />
          {deviceSource == "satellite-everywhere" && (
            <BasicSelect
              label="Device Type"
              value={deviceType}
              onChange={setDeviceType}
              required
              options={[{ value: "null", label: "Please Select a Type" },
              { value: "satellite-everywhere", label: "Satellite Device" }

          ]}
            />
        )}
          {deviceSource == "satellite-everywhere" && deviceType == "satellite-everywhere" && (
            <BasicSelect
              label="Source API"
              value={deviceSourceAPI}
              onChange={setDeviceSourceAPI}
              required
              options={[{ value: "null", label: "Please Select a Type" },
              { value: "everywhere-webhooks", label: "Everywhere Webhooks" },
              { value: "everywhere-mission-connect", label: "Mission Connect" }
          ]}
            />
          )}
          {deviceSource == "satellite-everywhere" && deviceType == "satellite-everywhere" && deviceSourceAPI == "everywhere-webhooks" && (
            <BasicText value={deviceID} onChange={setDeviceID} label="Device ID (Everywhere Device Name)" required />
          )}
          {deviceSource == "satellite-everywhere" && deviceType == "satellite-everywhere" && deviceSourceAPI == "everywhere-mission-connect" && (
            <BasicText value={deviceID} onChange={setDeviceID} label="Device ID (IMEI)" required />
          )}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            deviceType === "satellite-everywhere" 
              ? "max-h-[1000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    deviceType === "satellite-everywhere" && deviceSource === "satellite-everywhere"
                      ? "max-h-[1000px] opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className=" bg-white-blue p-4 space-y-3 rounded-lg mt-2 mb-4">
                    <BasicText value={imei} onChange={setImei} label="IMEI" />
                    <BasicText value={simCardPhoneNum} onChange={setSimCardPhoneNum} label="SIM Card Phone Number" />
                    <BasicText value={make} onChange={setMake} label="Make" />
                    <BasicText value={model} onChange={setModel} label="Model" />
                    <BasicText value={sourceID} onChange={setSourceID} label="Source ID" />
                    <BasicSelect
                      label="Secondary"
                      value={secondary ? "yes" : "no"}
                      onChange={(v) => setSecondary(v === "yes")}
                      options={[
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                      ]}
                    />
                    <BasicText value={oemSerial} onChange={setOemSerial} label="OEM Serial" />
                    <BasicText value={apiHubSerial} onChange={setApiHubSerial} label="API Hub Serial" />
                    <BasicText value={apiSource} onChange={setApiSource} label="API Source" />
                  </div>
                </div>
        </div>
          </div>
        </div>
        <div className="flex flex-col gap-8 flex-1">
          <div className="bg-white shadow-md p-4 flex flex-col gap-8">
            <div>
              <label className="mb-1">Department</label>
              {(auth.hasAccessLevelClient || (auth.hasAccessLevelDepartment && id != auth.userID)) && (
                                <DepartmentSelectSingle department={department} onChange={setDepartment}
                                                        initialDepartmentID={departmentID}
                                                        clientID={clientID}/>
                            )}            
                            </div>
            <p className="text-md">Assigned User</p>
            <UserSelectSingleV2
            user={selectedUser}
            initialUserID={assignedUserID != "000000000000000000000000" ? assignedUserID : undefined}
            onSelectionChange={handleUserChange}
            clientID={initialClientID}
            departmentID={department?.id}
            />

          {selectedUser && (
            <>
          <p className="text-md">User Assignment End Date</p>
          <div className="flex items-center gap-2 mb-12">
            <BasicText
              value={endDate}
              type={"date"}
              onChange={val => {
                setEndDate(val);
                setAssignmentEndDateTouched(true);
              }}
              placeholder={"End Date"}
              className={"flex-grow"}
            />
            {endDate && (
              <button
                type="button"
                className="px-2 py-1 bg-blue text-white rounded hover:bg-gray-300 whitespace-nowrap"
                onClick={() => setEndDate("")}
              >
                Clear Date
              </button>
            )}
          </div>

            </>
          )}



            <BlueButton text="Update Device" disabled={saving} onClick={save} loading={saving} />
            {errors.length > 0 && (
              <div className="flex flex-col gap-2">
                {errors.map((e, i) => (
                  <p key={i} className="text-center text-red">
                    {e}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

DeviceEdit.displayName = "DeviceEdit";
export default DeviceEdit;
